import React, { useState, useEffect } from "react";
import {
  Store,
  Package,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  Globe,
  ChevronUp,
  ChevronsUpDown,
  X,
} from "lucide-react";
import { api } from "../../services/api";
import Pagination from "../../components/Pagination";
import {
  Store as StoreType,
  WooCommerceOrder,
  SmartSystemStatus,
  TabType,
  getOrdersByDomain,
  getOrderStats,
  OrderFilters,
  PaginationState,
  filterAndSortOrders,
} from "./types";

const WooCommerceMain: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("orders");
  const [stores, setStores] = useState<StoreType[]>([]);
  const [orders, setOrders] = useState<WooCommerceOrder[]>([]);
  const [ordersByDomain, setOrdersByDomain] = useState<
    Record<string, WooCommerceOrder[]>
  >({});
  const [smartStatus, setSmartStatus] = useState<SmartSystemStatus | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters and pagination for each domain
  const [domainFilters, setDomainFilters] = useState<
    Record<string, OrderFilters>
  >({});
  const [domainPagination, setDomainPagination] = useState<
    Record<string, PaginationState>
  >({});
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(
    new Set()
  );
  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log("🔄 [WooCommerce] Loading data from API...");

      // Load stores
      const storesResponse = await api.getWooCommerceStores();
      if (storesResponse.success) {
        setStores(storesResponse.stores || []);
        console.log(
          `🏪 [WooCommerce] Loaded ${storesResponse.stores?.length || 0} stores`
        );
      }

      // Load orders
      const ordersResponse = await api.getWooCommerceOrders();
      if (ordersResponse.success) {
        const ordersData = ordersResponse.orders || [];
        setOrders(ordersData);
        const groupedOrders = getOrdersByDomain(ordersData);
        setOrdersByDomain(groupedOrders);
        console.log(`📦 [WooCommerce] Loaded ${ordersData.length} orders`);
      }

      // Load smart status (optional)
      try {
        const statusResponse = await api.getWooCommerceSmartStatus();
        if (statusResponse.success) {
          setSmartStatus(statusResponse.status);
        }
      } catch (statusErr) {
        console.warn("Smart status not available yet:", statusErr);
        // Don't set error for smart status, it's optional
      }
    } catch (err) {
      console.error("❌ [WooCommerce] Error loading data:", err);
      setError("Failed to load WooCommerce data");
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Initialize filters and pagination for each domain
  useEffect(() => {
    const domains = Object.keys(ordersByDomain);
    const newFilters: Record<string, OrderFilters> = {};
    const newPagination: Record<string, PaginationState> = {};

    domains.forEach((domain) => {
      if (!domainFilters[domain]) {
        newFilters[domain] = {
          search: "",
          status: "",
          sortBy: "createdAt",
          sortOrder: "desc",
        };
      }
      if (!domainPagination[domain]) {
        newPagination[domain] = {
          currentPage: 1,
          itemsPerPage: 50,
        };
      }
    });

    if (Object.keys(newFilters).length > 0) {
      setDomainFilters((prev) => ({ ...prev, ...newFilters }));
    }
    if (Object.keys(newPagination).length > 0) {
      setDomainPagination((prev) => ({ ...prev, ...newPagination }));
    }
  }, [ordersByDomain]);

  const handleManualSync = async (storeId: string, forceFullSync = false) => {
    try {
      setSyncing(storeId);
      setError(null);

      console.log(
        `🔄 [WooCommerce] Starting manual sync for store ${storeId}...`
      );

      const response = await api.syncWooCommerceStore(storeId, forceFullSync);
      if (response.success) {
        console.log(`✅ [WooCommerce] Sync completed for store ${storeId}`);
        // Reload data after sync
        await loadData();
      } else {
        setError(response.message || "Sync failed");
      }
    } catch (err) {
      console.error("❌ [WooCommerce] Sync error:", err);
      setError("Failed to sync store");
    } finally {
      setSyncing(null);
    }
  };

  const handleAddStore = () => {
    // TODO: Implement add store functionality
    console.log("Add store clicked");
  };

  const handleEditStore = (store: StoreType) => {
    // TODO: Implement edit store functionality
    console.log("Edit store clicked:", store);
  };

  const handleDeleteStore = async (storeId: string) => {
    try {
      console.log(`🗑️ [WooCommerce] Deleting store ${storeId}...`);

      const response = await api.deleteWooCommerceStore(storeId);
      if (response.success) {
        console.log(`✅ [WooCommerce] Store ${storeId} deleted successfully`);
        // Reload data after deletion
        await loadData();
      } else {
        setError(response.message || "Failed to delete store");
      }
    } catch (err) {
      console.error("❌ [WooCommerce] Delete error:", err);
      setError("Failed to delete store");
    }
  };

  const updateDomainFilter = (
    domain: string,
    newFilters: Partial<OrderFilters>
  ) => {
    setDomainFilters((prev) => ({
      ...prev,
      [domain]: { ...prev[domain], ...newFilters },
    }));
    // Reset to first page when filters change
    setDomainPagination((prev) => ({
      ...prev,
      [domain]: { ...prev[domain], currentPage: 1 },
    }));
  };

  const updateDomainPagination = (
    domain: string,
    newPagination: Partial<PaginationState>
  ) => {
    setDomainPagination((prev) => ({
      ...prev,
      [domain]: { ...prev[domain], ...newPagination },
    }));
  };

  const getFilteredOrdersForDomain = (domain: string) => {
    const domainOrders = ordersByDomain[domain] || [];
    const filters = domainFilters[domain] || {
      search: "",
      status: "",
      sortBy: "createdAt" as const,
      sortOrder: "desc" as const,
    };
    return filterAndSortOrders(domainOrders, filters);
  };

  const getPaginatedOrdersForDomain = (domain: string) => {
    const filteredOrders = getFilteredOrdersForDomain(domain);
    const pagination = domainPagination[domain] || {
      currentPage: 1,
      itemsPerPage: 50,
    };

    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;

    return filteredOrders.slice(startIndex, endIndex);
  };

  const handleSort = (domain: string, sortBy: string) => {
    const currentFilters = domainFilters[domain] || {
      search: "",
      status: "",
      sortBy: "createdAt",
      sortOrder: "desc",
    };

    let newSortOrder: "asc" | "desc" = "asc";

    if (currentFilters.sortBy === sortBy) {
      // If same field, cycle through: asc -> desc -> default (createdAt desc)
      if (currentFilters.sortOrder === "asc") {
        newSortOrder = "desc";
      } else if (currentFilters.sortOrder === "desc") {
        // Third click: reset to default
        sortBy = "createdAt";
        newSortOrder = "desc";
      }
    }

    updateDomainFilter(domain, {
      sortBy: sortBy as any,
      sortOrder: newSortOrder,
    });
  };

  const getSortIcon = (domain: string, sortBy: string) => {
    const filters = domainFilters[domain] || {
      search: "",
      status: "",
      sortBy: "createdAt",
      sortOrder: "desc",
    };

    if (filters.sortBy !== sortBy) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }

    return filters.sortOrder === "asc" ? (
      <ChevronUp className="w-4 h-4 text-[#264a3e]" />
    ) : (
      <ChevronDown className="w-4 h-4 text-[#264a3e]" />
    );
  };

  const toggleDomainExpansion = (domain: string) => {
    setExpandedDomains((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(domain)) {
        newSet.delete(domain);
      } else {
        newSet.add(domain);
      }
      return newSet;
    });
  };

  const handleSearchInput = (domain: string, value: string) => {
    setSearchInputs((prev) => ({
      ...prev,
      [domain]: value,
    }));
  };

  const handleSearch = (domain: string) => {
    const searchValue = searchInputs[domain] || "";
    updateDomainFilter(domain, { search: searchValue });
  };

  const clearSearch = (domain: string) => {
    setSearchInputs((prev) => ({
      ...prev,
      [domain]: "",
    }));
    updateDomainFilter(domain, { search: "" });
  };

  const getFilteredOrderCount = (domain: string) => {
    const domainOrders = ordersByDomain[domain] || [];
    const filters = domainFilters[domain] || {
      search: "",
      status: "",
      sortBy: "createdAt" as const,
      sortOrder: "desc" as const,
    };
    return filterAndSortOrders(domainOrders, filters).length;
  };

  const stats = getOrderStats(orders);

  const tabs = [
    {
      id: "orders" as TabType,
      label: "Orders by Domain",
      icon: Package,
      count: orders.length,
    },
    {
      id: "stores" as TabType,
      label: "Stores",
      icon: Store,
      count: stores.length,
    },
  ];

  const customButtons = [
    {
      id: "refresh",
      label: "Refresh",
      icon: RefreshCw,
      onClick: loadData,
      variant: "primary" as const,
      disabled: false,
    },
  ];

  const renderOrdersTab = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
          <span className="ml-2 text-gray-600">Loading orders...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      );
    }

    if (Object.keys(ordersByDomain).length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Orders Found
          </h3>
          <p className="text-gray-600">
            No WooCommerce orders have been synced yet. Orders will appear here
            once stores are configured and syncing begins.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {Object.entries(ordersByDomain).map(([domain, domainOrders]) => {
          const filters = domainFilters[domain] || {
            search: "",
            status: "",
            sortBy: "createdAt" as const,
            sortOrder: "desc" as const,
          };
          const pagination = domainPagination[domain] || {
            currentPage: 1,
            itemsPerPage: 50,
          };
          const filteredOrders = getFilteredOrdersForDomain(domain);
          const paginatedOrders = getPaginatedOrdersForDomain(domain);
          const totalPages = Math.ceil(
            filteredOrders.length / pagination.itemsPerPage
          );
          const isExpanded = expandedDomains.has(domain);

          // Get unique statuses for filter dropdown
          const uniqueStatuses = Array.from(
            new Set(domainOrders.map((order) => order.status))
          );

          return (
            <div
              key={domain}
              className="bg-white rounded-lg shadow overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <Globe className="w-5 h-5 text-[#264a3e] mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">
                      {domain}
                    </h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="bg-[#264a3e] text-white px-3 py-1 rounded-full text-sm font-medium">
                      {getFilteredOrderCount(domain)} orders
                    </span>
                    {/* Accordion Toggle Button */}
                    <button
                      onClick={() => toggleDomainExpansion(domain)}
                      className="inline-flex items-center px-3 py-1.5 border border-[#264a3e] text-sm font-medium rounded-md text-[#264a3e] bg-white hover:bg-[#264a3e] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Filters - Only show when accordion is expanded */}
                {isExpanded && (
                  <div className="flex flex-wrap gap-4 justify-between">
                    {/* Search */}
                    <div className="flex items-center gap-4 space-x-2 flex-1 min-w-[300px] max-w-[500px]">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search orders..."
                          value={searchInputs[domain] || ""}
                          onChange={(e) =>
                            handleSearchInput(domain, e.target.value)
                          }
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSearch(domain)
                          }
                          className="pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent w-full"
                        />
                        {filters.search && (
                          <button
                            onClick={() => clearSearch(domain)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => handleSearch(domain)}
                        className="px-4 py-2 bg-[#264a3e] text-white rounded-md text-sm font-medium hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-[#264a3e] transition-colors"
                      >
                        Search
                      </button>
                    </div>

                    {/* Status Filter */}
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <select
                        value={filters.status}
                        onChange={(e) =>
                          updateDomainFilter(domain, { status: e.target.value })
                        }
                        className="pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#264a3e] focus:border-transparent appearance-none bg-white"
                      >
                        <option value="">All Statuses</option>
                        {uniqueStatuses.map((status) => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                    </div>
                  </div>
                )}
              </div>

              {/* Accordion Content */}
              {isExpanded && (
                <div className="bg-gray-50">
                  {/* Orders Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort(domain, "orderNumber")}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Order</span>
                              {getSortIcon(domain, "orderNumber")}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort(domain, "status")}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Status</span>
                              {getSortIcon(domain, "status")}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort(domain, "customerEmail")}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Customer</span>
                              {getSortIcon(domain, "customerEmail")}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort(domain, "total")}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Total</span>
                              {getSortIcon(domain, "total")}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={() => handleSort(domain, "createdAt")}
                          >
                            <div className="flex items-center space-x-1">
                              <span>Date</span>
                              {getSortIcon(domain, "createdAt")}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedOrders.map((order) => (
                          <tr key={order._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Package className="w-4 h-4 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    #{order.orderNumber}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    ID: {order.woocommerceId}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  order.status === "completed"
                                    ? "bg-green-100 text-green-800"
                                    : order.status === "processing"
                                    ? "bg-blue-100 text-blue-800"
                                    : order.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {order.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {order.customerEmail}
                                  </div>
                                  {order.customerPhone && (
                                    <div className="text-sm text-gray-500">
                                      {order.customerPhone}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {order.total.toFixed(2)}{" "}
                                {order.currency.toUpperCase()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {new Date(order.createdAt).toLocaleDateString()}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="bg-white">
                    <Pagination
                      currentPage={pagination.currentPage}
                      totalPages={totalPages}
                      totalItems={filteredOrders.length}
                      itemsPerPage={pagination.itemsPerPage}
                      onPageChange={(page) =>
                        updateDomainPagination(domain, { currentPage: page })
                      }
                      showFirstLast={true}
                      maxVisiblePages={5}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderStoresTab = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#264a3e]"></div>
          <span className="ml-2 text-gray-600">Loading stores...</span>
        </div>
      );
    }

    return (
      <>
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Stores Management */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">
                  WooCommerce Stores
                </h2>
                <p className="text-sm text-gray-600">
                  Manage your WooCommerce store connections and sync settings
                </p>
              </div>
              <button
                onClick={handleAddStore}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
              >
                <Store className="w-4 h-4 mr-2" />
                Add Store
              </button>
            </div>
          </div>

          <div className="p-6">
            {stores.length === 0 ? (
              <div className="text-center py-8">
                <Store className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Stores Configured
                </h3>
                <p className="text-gray-600 mb-4">
                  Add your first WooCommerce store to start syncing orders.
                </p>
                <button
                  onClick={handleAddStore}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32]"
                >
                  <Store className="w-4 h-4 mr-2" />
                  Add Store
                </button>
              </div>
            ) : (
              <div className="grid gap-6">
                {stores.map((store) => (
                  <div
                    key={store.id}
                    className="border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <Store className="w-5 h-5 text-[#264a3e] mr-2" />
                          <h3 className="text-lg font-medium text-gray-900">
                            {store.name}
                          </h3>
                          <div className="ml-3 flex items-center">
                            {store.enabled ? (
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                            ) : (
                              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-1"></div>
                            )}
                            <span
                              className={`text-sm font-medium ${
                                store.enabled
                                  ? "text-green-700"
                                  : "text-yellow-700"
                              }`}
                            >
                              {store.enabled ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center text-sm text-gray-600 mb-3">
                          <Globe className="w-4 h-4 mr-1" />
                          <span>{store.baseUrl}</span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">
                              Tracking Interval:
                            </span>
                            <div className="font-medium">
                              {store.trackingInterval / 1000}s
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Batch Size:</span>
                            <div className="font-medium">{store.batchSize}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Last Sync:</span>
                            <div className="font-medium">
                              {store.lastSync
                                ? new Date(store.lastSync).toLocaleString()
                                : "Never"}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Created:</span>
                            <div className="font-medium">
                              {new Date(store.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleManualSync(store.id)}
                          disabled={syncing === store.id}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] disabled:opacity-50"
                        >
                          {syncing === store.id ? (
                            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4 mr-1" />
                          )}
                          Sync
                        </button>

                        <button
                          onClick={() => handleEditStore(store)}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e]"
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDeleteStore(store.id)}
                          className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "orders":
        return renderOrdersTab();
      case "stores":
        return renderStoresTab();
      default:
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Select a tab to view content</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Store className="w-8 h-8 mr-3 text-[#264a3e]" />
                WooCommerce Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage WooCommerce stores, sync orders, and monitor system
                status
              </p>
            </div>

            {/* Custom Buttons */}
            {customButtons.length > 0 && (
              <div className="flex items-center space-x-3 ml-6">
                {customButtons.map((button) => {
                  const ButtonIcon = button.icon;
                  return (
                    <button
                      key={button.id}
                      onClick={button.onClick}
                      disabled={button.disabled}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
                    >
                      {ButtonIcon && <ButtonIcon className="w-4 h-4 mr-2" />}
                      {button.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Store className="h-6 w-6 text-[#264a3e]" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Stores
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalStores}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Store className="h-6 w-6 text-green-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Active Stores
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.activeStores}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-blue-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Orders
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {stats.totalOrders}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Package className="h-6 w-6 text-purple-500" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Domains
                    </dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {Object.keys(ordersByDomain).length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`${
                      activeTab === tab.id
                        ? "border-[#264a3e] text-[#264a3e]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <TabIcon className="w-4 h-4 mr-2" />
                    {tab.label}
                    <span className="bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs ml-2">
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>{renderTabContent()}</div>
      </div>
    </div>
  );
};

export default WooCommerceMain;
